import { PosturalGridOverlay } from "@/components/PosturalGrid";
import { useMemo, useRef, useState } from "react";
import { Activity, CalendarDays, ChevronDown, FileText, Images, Layers2 } from "lucide-react";
import PinchZoomFrame from "@/components/PinchZoomFrame";

const VIEWS = [
  { key: "front", label: "Frontal" },
  { key: "side", label: "Lateral" },
  { key: "back", label: "Posterior" },
];

const SEGMENT_LABELS = {
  feet: "Pés",
  knees: "Joelhos",
  pelvis: "Bacia",
  spine: "Coluna",
  spine_lateral: "Curvas da coluna (vista lateral)",
  shoulders: "Ombros",
  cervical: "Cervical",
  scapula: "Omoplatas",
};

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Data indisponível" : date.toLocaleDateString("pt-PT");
}

function formatChartDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });
}

function scoreOf(assessment) {
  const value = assessment?.analysis?.global_score;
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
    ? Number(value)
    : null;
}

function TrendChart({ assessments }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const pointRefs = useRef([]);
  const points = assessments
    .map((item, index) => ({ date: item.created_at, value: scoreOf(item), index }))
    .filter((item) => item.value != null)
    .map((item, index) => ({ ...item, index }));
  if (points.length < 3) return null;

  const width = 360;
  const height = 92;
  const leftInset = 28;
  const rightInset = 12;
  const topInset = 10;
  const bottomInset = 25;
  const plotHeight = height - topInset - bottomInset;
  const xStep = (width - leftInset - rightInset) / (points.length - 1);
  const coordinates = points.map((point, index) => ({
    x: leftInset + xStep * index,
    y: topInset + (1 - Math.max(0, Math.min(100, point.value)) / 100) * plotHeight,
    ...point,
  }));
  const path = coordinates.map((point, index) => `${index ? "L" : "M"} ${point.x} ${point.y}`).join(" ");
  const selected = coordinates[selectedIndex ?? coordinates.length - 1];
  const choosePoint = (index) => setSelectedIndex(index);
  const handlePointKeyDown = (event, index) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choosePoint(index);
    }
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const nextIndex = event.key === "ArrowRight"
        ? Math.min(coordinates.length - 1, index + 1)
        : Math.max(0, index - 1);
      choosePoint(nextIndex);
      pointRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <figure className="mt-5 rounded-xl border border-slate-700/70 bg-slate-950/30 p-4 sm:p-5" data-testid="longitudinal-trend">
      <figcaption className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-xs font-medium text-slate-300">Indicador global registado</span>
        <span className="text-[11px] text-slate-300">Escala 0–100 · sem interpretação automática</span>
      </figcaption>
      <svg viewBox={`0 0 ${width} ${height + 8}`} role="group" aria-label={`Indicador global, escala de 0 a 100, ${points.length} registos. Utilize as setas para percorrer os pontos.`} className="h-28 w-full overflow-visible">
        {[0, 50, 100].map((value) => {
          const y = topInset + (1 - value / 100) * plotHeight;
          return (
            <g key={value} aria-hidden="true">
              <line x1={leftInset} x2={width - rightInset} y1={y} y2={y} stroke="var(--portal-border)" strokeDasharray={value === 50 ? "2 4" : "none"} strokeWidth="1" />
              <text x="0" y={y + 3} fill="var(--portal-muted)" fontSize="9">{value}</text>
            </g>
          );
        })}
        <path d={path} fill="none" stroke="var(--portal-info)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {coordinates.map((point) => (
          <g key={`${point.date}-${point.index}`}>
            <text x={point.x} y={height - 3} textAnchor={point.index === 0 ? "start" : point.index === coordinates.length - 1 ? "end" : "middle"} fill="var(--portal-muted)" fontSize="9">
              {formatChartDate(point.date)}
            </text>
            <circle
              cx={point.x}
              cy={point.y}
              r={selected?.index === point.index ? "5" : "3.5"}
              fill={selected?.index === point.index ? "var(--portal-positive)" : "var(--portal-info)"}
              stroke="var(--portal-bg)"
              strokeWidth="2"
              role="button"
              tabIndex={0}
              ref={(element) => { pointRefs.current[point.index] = element; }}
              data-point-index={point.index}
              aria-label={`${formatDate(point.date)}: indicador ${point.value} de 100`}
              aria-pressed={selected?.index === point.index}
              onClick={() => choosePoint(point.index)}
              onKeyDown={(event) => handlePointKeyDown(event, point.index)}
              className="cursor-pointer outline-none focus-visible:stroke-white focus-visible:stroke-[3]"
            />
          </g>
        ))}
      </svg>
      {selected && (
        <p className="mt-1 text-sm font-medium text-slate-200" aria-live="polite" data-testid="longitudinal-trend-detail">
          Valor registado: {selected.value} / 100 · {formatDate(selected.date)}
        </p>
      )}
      <p className="mt-2 text-[11px] leading-relaxed text-slate-300">
        O indicador descreve apenas os valores registados em cada data. Valores superiores ou inferiores não representam, por si só, melhoria ou eficácia.
      </p>
    </figure>
  );
}

function PhotoPanel({ assessment, view, gridVisible, gridSize, gridOpacity, zoom }) {
  const image = assessment?.images?.[view];
  return (
    <div className="relative aspect-[3/4] overflow-hidden rounded-xl border border-slate-700 bg-slate-950" data-testid={`comparison-photo-${view}`}>
      {image ? (
        zoom ? (
          <PinchZoomFrame enabled className="h-full w-full">
            <img src={image} alt={`Imagem ${VIEWS.find((item) => item.key === view)?.label.toLowerCase()} de ${formatDate(assessment.created_at)}`} className="h-full w-full object-contain" />
          </PinchZoomFrame>
        ) : (
          <img src={image} alt={`Imagem ${VIEWS.find((item) => item.key === view)?.label.toLowerCase()} de ${formatDate(assessment.created_at)}`} className="h-full w-full object-contain" />
        )
      ) : (
        <div className="flex h-full items-center justify-center px-5 text-center text-sm text-slate-400">
          Imagem não disponível para esta perspetiva.
        </div>
      )}
      {gridVisible && image && <PosturalGridOverlay opacity={gridOpacity} testId={`comparison-grid-${view}`} />}
    </div>
  );
}

export function Comparison({ before, after, gridVisible, onGridVisibleChange, gridSize, gridOpacity, onGridOpacityChange, labels = ["Antes", "Depois"], title = "Comparação visual", testId = "longitudinal-comparison" }) {
  const [view, setView] = useState("front");
  const [mode, setMode] = useState("side-by-side");
  const r = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Math.round(Number(v)));
  const beforeScore = r(scoreOf(before));
  const afterScore = r(scoreOf(after));
  const scoreDelta = beforeScore == null || afterScore == null ? null : afterScore - beforeScore;
  const beforeSegments = before?.analysis?.segments || {};
  const afterSegments = after?.analysis?.segments || {};
  const commonSegments = Object.keys(afterSegments).filter((key) => (
    beforeSegments[key]?.score != null && afterSegments[key]?.score != null
  ));

  return (
    <div className="mt-5 border-t border-slate-700/60 pt-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg text-slate-100">{title}</h3>
          <p className="mt-1 text-xs text-slate-400">As imagens mantêm a proporção original; não são realinhadas.</p>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Perspetiva das imagens">
          {VIEWS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setView(item.key)}
              aria-pressed={view === item.key}
              className={`min-h-10 rounded-full border px-3 text-xs transition ${view === item.key ? "border-sky-400/50 bg-sky-400/10 text-sky-100" : "border-slate-700 text-slate-300"}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="inline-flex min-h-10 items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={gridVisible}
            onChange={(event) => onGridVisibleChange(event.target.checked)}
            className="h-4 w-4 accent-sky-400"
          />
          Grelha postural
        </label>
        <label className="inline-flex min-h-10 items-center gap-2 text-xs text-slate-300">
          Intensidade
          <input
            type="range"
            min="0"
            max="100"
            value={gridOpacity}
            onChange={(event) => onGridOpacityChange(Number(event.target.value))}
            aria-label="Intensidade da grelha de comparação"
            aria-valuetext={`${gridOpacity}%`}
            className="w-24 accent-sky-400"
          />
          <output className="w-9 text-right font-mono">{gridOpacity}%</output>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Modo de comparação fotográfica">
        {[
          { key: "before", label: labels[0] },
          { key: "after", label: labels[1] },
          { key: "side-by-side", label: "Lado a lado" },
        ].map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setMode(option.key)}
            aria-pressed={mode === option.key}
            className={`min-h-10 rounded-full border px-3 text-xs transition ${mode === option.key ? "border-sky-400/50 bg-sky-400/15 font-medium text-sky-100" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {mode === "side-by-side" ? (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:gap-4">
        {[{ item: before, label: labels[0] }, { item: after, label: labels[1] }].map(({ item, label }) => (
          <div key={`${label}-${item?.id}`}>
            <div className="mb-2 flex min-h-9 flex-col justify-between gap-1 text-[11px] sm:min-h-6 sm:flex-row sm:items-center sm:gap-2 sm:text-xs">
              <span className="font-medium text-slate-200">{label}</span>
              <time className="text-slate-300" dateTime={item?.created_at}>{formatDate(item?.created_at)}</time>
            </div>
            <PhotoPanel assessment={item} view={view} gridVisible={gridVisible} gridSize={gridSize} gridOpacity={gridOpacity / 100} zoom />
          </div>
        ))}
        </div>
      ) : (
        <div className="mt-4 mx-auto w-full max-w-md">
          <div className="mb-2 flex min-h-9 items-center justify-between gap-2 text-xs">
            <span className="font-medium text-slate-200">{mode === "before" ? labels[0] : labels[1]}</span>
            <time className="text-slate-300" dateTime={mode === "before" ? before?.created_at : after?.created_at}>
              {formatDate(mode === "before" ? before?.created_at : after?.created_at)}
            </time>
          </div>
          <PhotoPanel
            assessment={mode === "before" ? before : after}
            view={view}
            gridVisible={gridVisible}
            gridSize={gridSize}
            gridOpacity={gridOpacity / 100}
            zoom
          />
        </div>
      )}

      <section className="mt-5 rounded-xl border border-slate-700/70 bg-slate-950/30 p-4" aria-labelledby="functional-comparison-heading">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-sky-300" />
          <h4 id="functional-comparison-heading" className="text-sm font-medium text-slate-100">Indicadores funcionais disponíveis</h4>
        </div>
        {scoreDelta == null && commonSegments.length === 0 ? (
          <p className="mt-3 text-sm text-slate-400">Não existem indicadores partilhados entre estes momentos.</p>
        ) : (
          <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {scoreDelta != null && (
              <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-900/70 px-3 py-2">
                <dt className="text-sm text-slate-300">Indicador global</dt>
                <dd className="text-sm font-mono text-slate-100">
                  {beforeScore} → {afterScore} ({scoreDelta > 0 ? "+" : ""}{scoreDelta})
                </dd>
              </div>
            )}
            {commonSegments.map((key) => {
              const beforeValue = Math.round(Number(beforeSegments[key].score));
              const afterValue = Math.round(Number(afterSegments[key].score));
              const delta = afterValue - beforeValue;
              return (
                <div key={key} className="flex items-center justify-between gap-3 rounded-lg bg-slate-900/70 px-3 py-2">
                  <dt className="text-sm text-slate-300">{SEGMENT_LABELS[key] || key}</dt>
                  <dd className="text-sm font-mono text-slate-100">
                    {beforeValue} → {afterValue} ({delta > 0 ? "+" : ""}{delta})
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
        <p className="mt-3 text-[11px] leading-relaxed text-slate-300">
          Uma diferença numérica, por si só, não determina melhoria clínica nem eficácia terapêutica. A interpretação cabe ao profissional no contexto do acompanhamento.
        </p>
      </section>
    </div>
  );
}

export default function PatientLongitudinal({ assessments = [], gridPreference, onGridPreferenceChange }) {
  const ordered = useMemo(
    () => [...assessments].filter((item) => item?.id && item?.created_at).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [assessments],
  );
  const latest = ordered[ordered.length - 1] || null;
  const earlier = ordered.slice(0, -1);
  const [beforeId, setBeforeId] = useState("");
  const [afterId, setAfterId] = useState("");
  const before = ordered.find((item) => item.id === beforeId) || (ordered.length > 1 ? ordered[ordered.length - 2] : null);
  const after = ordered.find((item) => item.id === afterId) || latest;
  const canCompare = ordered.length > 1 && before && after && before.id !== after.id;

  const chooseBefore = (id) => {
    setBeforeId(id);
    if (id === after?.id) setAfterId(latest?.id === id ? ordered[0]?.id : latest?.id || "");
  };
  const chooseAfter = (id) => {
    setAfterId(id);
    if (id === before?.id) setBeforeId(earlier.find((item) => item.id !== id)?.id || "");
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/65 p-5 shadow-xl shadow-black/10 sm:p-7" data-testid="portal-longitudinal">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-400/10 text-sky-300">
          <Layers2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-300">Percurso longitudinal</div>
          <h2 className="mt-1 font-display text-xl text-slate-100 sm:text-2xl">Avaliações e comparação</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-300">
            Apenas avaliações partilhadas individualmente pelo profissional. Os indicadores são apresentados sem conclusões automáticas.
          </p>
        </div>
      </div>

      {!ordered.length ? (
        <p className="mt-5 rounded-xl border border-slate-700 p-4 text-sm text-slate-300" data-testid="longitudinal-empty">
          Ainda não existem avaliações disponíveis para apresentar.
        </p>
      ) : (
        <>
          <ol
            className="mt-4 flex snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain pb-2 sm:mt-5 sm:gap-3"
            aria-label="Linha temporal horizontal de avaliações"
            tabIndex={0}
          >
            {ordered.map((item, index) => {
              const isLatest = item.id === latest?.id;
              const selectedRole = item.id === before?.id
                ? "Avaliação inicial"
                : item.id === after?.id
                  ? "Avaliação posterior"
                  : null;
              return (
                <li key={item.id} className="w-36 shrink-0 snap-start sm:w-40">
                  <button
                    type="button"
                    onClick={() => isLatest ? chooseAfter(item.id) : chooseBefore(item.id)}
                    aria-pressed={Boolean(selectedRole)}
                    className={`flex min-h-[4.75rem] w-full flex-col items-start justify-between gap-2 rounded-xl border px-3 py-2 text-left transition ${selectedRole ? "border-sky-400/45 bg-sky-400/[0.09]" : "border-slate-700/70 bg-slate-950/20 hover:border-slate-600"}`}
                    aria-label={`${formatDate(item.created_at)}, ${isLatest ? "avaliação mais recente" : `avaliação ${index + 1}`}`}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <CalendarDays className="h-4 w-4 shrink-0 text-sky-300" />
                      {isLatest && <span className="text-[10px] font-medium text-sky-200">Mais recente</span>}
                    </span>
                    <span className="w-full">
                      <time className="block text-sm font-medium text-slate-100" dateTime={item.created_at}>{formatDate(item.created_at)}</time>
                      <span className="mt-1 block truncate text-[11px] text-slate-300">
                        {selectedRole || item.plan_summary || "Avaliação funcional"}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          {ordered.length === 1 ? (
            <p className="mt-4 rounded-xl border border-slate-700/70 bg-slate-950/30 p-4 text-sm text-slate-300" data-testid="longitudinal-single">
              Existe uma avaliação. Quando houver outro registo autorizado, será possível comparar os dois momentos.
            </p>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-xs text-slate-300">
                  Avaliação inicial
                  <select value={before?.id || ""} onChange={(event) => chooseBefore(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                    {ordered.map((item) => <option key={item.id} value={item.id} disabled={item.id === after?.id || new Date(item.created_at) >= new Date(after?.created_at)}>{formatDate(item.created_at)}</option>)}
                  </select>
                </label>
                <label className="text-xs text-slate-300">
                  Avaliação posterior
                  <select value={after?.id || ""} onChange={(event) => chooseAfter(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100">
                    {ordered.map((item) => <option key={item.id} value={item.id} disabled={item.id === before?.id || new Date(item.created_at) <= new Date(before?.created_at)}>{formatDate(item.created_at)}{item.id === latest?.id ? " · mais recente" : ""}</option>)}
                  </select>
                </label>
              </div>

              {canCompare && (
                <Comparison
                  before={before}
                  after={after}
                  gridVisible={gridPreference.visible}
                  onGridVisibleChange={(visible) => onGridPreferenceChange((current) => ({ ...current, visible }))}
                  gridSize={32}
                  gridOpacity={gridPreference.intensity}
                  onGridOpacityChange={(intensity) => onGridPreferenceChange((current) => ({ ...current, intensity }))}
                />
              )}
            </>
          )}

          <TrendChart assessments={ordered} />

          <details className="group mt-5 border-t border-slate-700/60 pt-4" data-testid="longitudinal-documents">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-slate-300 marker:hidden [&::-webkit-details-marker]:hidden">
              <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Registos partilhados</span>
              <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open:rotate-180" />
            </summary>
            <div className="mt-3 space-y-2">
              {ordered.slice().reverse().map((item) => (
                <article key={`doc-${item.id}`} className="rounded-xl border border-slate-700/70 bg-slate-950/25 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-slate-200">
                    <Images className="h-4 w-4 text-sky-300" />
                    <time dateTime={item.created_at}>Avaliação de {formatDate(item.created_at)}</time>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">
                    Plano associado: {item.plan_summary || "Sem plano associado partilhado."}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.pdf_url ? "Relatório PDF disponível." : "Relatório PDF disponível apenas no link da respetiva avaliação."}
                  </p>
                </article>
              ))}
            </div>
          </details>
        </>
      )}
    </section>
  );
}
